import React, { useState, createContext, useEffect, useCallback, useRef } from 'react';
import Loading from 'app/components/MatxLoading';
import API from 'app/api';
import { getDefaultAuthBranding, mergeAuthBranding } from 'app/utils/authBranding';

const apiBase = `${process.env.REACT_APP_API_DOMAIN || ''}${process.env.REACT_APP_API_VERSION || ''}`;

const AuthContext = createContext({
    user: null,
    branding: getDefaultAuthBranding(),
    method: 'COGNITO_SESSION',
    signIn: () => Promise.resolve(),
    completeMfa: () => Promise.resolve(),
    getMfaStatus: () => Promise.resolve(),
    beginTotpSetup: () => Promise.resolve(),
    confirmTotpSetup: () => Promise.resolve(),
    disableTotp: () => Promise.resolve(),
    signOut: () => Promise.resolve(),
    signUp: () => Promise.resolve(),
    confirmSignUp: () => Promise.resolve(),
    signUpCognito: () => { },
    signInWithProvider: () => { },
    logout: () => Promise.resolve(),
    hardDeleteLogout: () => { },
    getToken: () => Promise.resolve(null),
    forgotPassword: () => Promise.resolve(),
    resetPassword: () => Promise.resolve(),
    resendVerification: () => Promise.resolve(),
    setProfilePicture: () => {},
    updatedUserDisplayName: () => Promise.resolve(),
});

const getUserByUserId = async () => {
    try {
        const { data } = await API.get('/GetUserByUserId');
        return data;
    } catch (error) {
        console.error('Error getting user by userId:', error);
        throw error;
    }
};

const updateUser = async (user, email) => {
    await API.put('/UpdateUser', user, { params: { Email: email } });
};

// Portal wording for the POST /auth/login/mfa errors. The API's own `message` is written
// for the AR app, so it is not shown here.
export const ACCOUNT_DEACTIVATED_MESSAGE =
    'This account has been deactivated. Contact your administrator to restore access.';

const MFA_LOGIN_ERRORS = {
    invalid_code: "That code didn't match. Check your authenticator app and try again.",
    session_expired: 'That took too long. Please sign in again.',
    account_deactivated: ACCOUNT_DEACTIVATED_MESSAGE,
};

// MFA enrollment endpoints (/auth/mfa*): cookie session, JSON in and out. A failure throws
// an Error whose `.code` is the API's `error` string (invalid_code, invalid_credentials,
// mfa_managed_by_idp, authentication_required, session_expired, invalid_access_token).
const mfaRequest = async (method, path, body) => {
    const response = await fetch(`${apiBase}${path}`, {
        method,
        credentials: 'include',
        ...(body && {
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const err = new Error(data.error || 'mfa_request_failed');
        err.code = data.error;
        throw err;
    }
    return data;
};

const enrichUser = (baseUser, argyleUserData) => {
    if (!argyleUserData) return baseUser;
    return {
        ...baseUser,
        id: argyleUserData.id ?? baseUser.id,
        firstName: argyleUserData.firstName ?? baseUser.firstName,
        lastName: argyleUserData.lastName ?? baseUser.lastName,
        userRole: argyleUserData.userRole ?? baseUser.userRole,
        displayName:
            argyleUserData.firstName && argyleUserData.lastName
                ? `${argyleUserData.firstName} ${argyleUserData.lastName}`
                : baseUser.displayName,
        name:
            argyleUserData.firstName && argyleUserData.lastName
                ? `${argyleUserData.firstName} ${argyleUserData.lastName}`
                : baseUser.name,
        avatar: argyleUserData.thumbnailURL ?? baseUser.avatar,
        hasProcoreIntegration: argyleUserData.hasProcoreIntegration ?? baseUser.hasProcoreIntegration,
    };
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [branding, setBranding] = useState(() => getDefaultAuthBranding());
    const [loading, setLoading] = useState(true);
    const isAuthenticated = user !== null;
    const userRef = useRef(user);
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const checkSession = useCallback(async () => {
        try {
            const response = await fetch(`${apiBase}/auth/session`, {
                credentials: 'include',
            });
            if (!response.ok) {
                setUser(null);
                return;
            }
            const data = await response.json();

            if (!data.authenticated || !data.user) {
                setUser(null);
                return;
            }

            const sessionUser = data.user;
            const baseUser = {
                id: sessionUser.userId,
                uid: sessionUser.userId,
                userId: sessionUser.userId,
                email: sessionUser.email ?? '',
                firstName: sessionUser.firstName ?? '',
                lastName: sessionUser.lastName ?? '',
                displayName:
                    sessionUser.firstName && sessionUser.lastName
                        ? `${sessionUser.firstName} ${sessionUser.lastName}`
                        : (sessionUser.email ?? ''),
                name:
                    sessionUser.firstName && sessionUser.lastName
                        ? `${sessionUser.firstName} ${sessionUser.lastName}`
                        : (sessionUser.email ?? ''),
                avatar: null,
                userRole: sessionUser.role ?? 'User',
                hasProcoreIntegration: false,
            };
            setUser(baseUser);

            try {
                const argyleUserData = await getUserByUserId();
                if (argyleUserData) {
                    setUser((prev) => enrichUser(prev || baseUser, argyleUserData));
                }
            } catch {
                // User row not found — keep base session user. In the session-based auth flow,
                // IdentityResolver provisions the user during /auth/callback, so a missing row
                // here indicates a transient DB error, not a first-sign-in provisioning case.
            }
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Direct email+password sign-in via our own API endpoint (no Cognito Hosted UI redirect).
    // Resolves to `{ challenge, session }` when the API answers 200 with a challenge instead
    // of a cookie (e.g. SOFTWARE_TOKEN_MFA → finish with completeMfa); otherwise undefined.
    const signIn = useCallback(async (email, password) => {
        const response = await fetch(`${apiBase}/auth/login`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (data.error === 'USER_NOT_CONFIRMED') {
                const err = new Error("Account exists but email hasn't been verified.");
                err.code = 'USER_NOT_CONFIRMED';
                throw err;
            }
            if (data.error === 'account_deactivated') {
                throw new Error(ACCOUNT_DEACTIVATED_MESSAGE);
            }
            throw new Error(
                data.error === 'invalid_credentials'
                    ? 'Invalid email or password.'
                    : 'Sign in failed. Please try again.',
            );
        }
        if (data.challenge) {
            return { challenge: data.challenge, session: data.session };
        }
        await checkSession();
    }, [checkSession]);

    // Second step of signIn: the authenticator-app code for a SOFTWARE_TOKEN_MFA challenge.
    // Resolves like signIn. Throws with `.code` of 'invalid_code' or 'session_expired'.
    const completeMfa = useCallback(async (email, session, code) => {
        const response = await fetch(`${apiBase}/auth/login/mfa`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, session, code }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const err = new Error(
                MFA_LOGIN_ERRORS[data.error] || 'Sign in failed. Please try again.',
            );
            err.code = data.error;
            throw err;
        }
        if (data.challenge) {
            return { challenge: data.challenge, session: data.session };
        }
        await checkSession();
    }, [checkSession]);

    const getMfaStatus = useCallback(() => mfaRequest('GET', '/auth/mfa'), []);

    const beginTotpSetup = useCallback(() => mfaRequest('POST', '/auth/mfa/totp/setup'), []);

    const confirmTotpSetup = useCallback(
        (code) => mfaRequest('POST', '/auth/mfa/totp/verify', { code }),
        [],
    );

    const disableTotp = useCallback(
        (password, code) => mfaRequest('DELETE', '/auth/mfa/totp', { password, code }),
        [],
    );

    const resendVerification = useCallback(async (email) => {
        const response = await fetch(`${apiBase}/auth/resend-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        if (response.status === 429) {
            const err = new Error('Too many attempts. Please wait a minute and try again.');
            err.code = 'RATE_LIMITED';
            throw err;
        }
        if (!response.ok) {
            throw new Error('Something went wrong. Please try again.');
        }
    }, []);

    // Same as signIn — Cognito Hosted UI has a "Create account" link
    const signUpCognito = useCallback(() => {
        const successUri = `${window.location.origin}/`;
        window.location.href = `${apiBase}/auth/signin?redirect_uri=${encodeURIComponent(successUri)}`;
    }, []);

    // Redirect directly to a specific IdP (e.g. "Google", "Microsoft") via the API's OAuth flow.
    // `returnTo` is a same-origin path to land on after the callback (defaults to the dashboard).
    const signInWithProvider = useCallback((providerName, returnTo) => {
        const successUri = `${window.location.origin}${returnTo || '/'}`;
        window.location.href = `${apiBase}/auth/signin?provider=${encodeURIComponent(providerName)}&redirect_uri=${encodeURIComponent(successUri)}`;
    }, []);

    // Register a new account via the API's signup endpoint
    const signUp = useCallback(async ({ email, password, firstName, lastName }) => {
        const response = await fetch(`${apiBase}/auth/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, firstName, lastName }),
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            const userExists =
                data.error === 'user_already_exists' ||
                (data.error === 'signup_failed' &&
                    data.message?.toLowerCase().includes('already exists'));
            throw new Error(
                userExists
                    ? 'An account with this email already exists.'
                    : 'Sign up failed. Please try again.',
            );
        }
    }, []);

    const signOut = useCallback(async () => {
        try {
            await fetch(`${apiBase}/auth/signout`, {
                method: 'POST',
                credentials: 'include',
            });
        } catch {
            // best effort — session will expire naturally
        }
        setUser(null);
        window.location.href = '/session/signin';
    }, []);

    const getToken = useCallback(async () => {
        try {
            const response = await fetch(`${apiBase}/auth/token`, {
                credentials: 'include',
            });
            if (!response.ok) return null;
            const data = await response.json();
            return data.access_token ?? null;
        } catch {
            return null;
        }
    }, []);

    const confirmSignUp = useCallback(async (email, code) => {
        const response = await fetch(`${apiBase}/auth/confirm-signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code }),
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.message || 'Verification failed. Please try again.');
        }
    }, []);

    const forgotPassword = useCallback(async (email) => {
        const response = await fetch(`${apiBase}/auth/forgot-password`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || 'forgot_password_failed');
        }
    }, []);

    const resetPassword = useCallback(async (email, code, newPassword) => {
        const response = await fetch(`${apiBase}/auth/confirm-password-reset`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code, newPassword }),
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || 'reset_password_failed');
        }
    }, []);

    // POST /UploadUserPhoto already stores both photo URLs on the user row; this only refreshes
    // the avatar shown in the portal.
    const setProfilePicture = useCallback((thumbnailURL) => {
        setUser((prev) => (prev ? { ...prev, avatar: thumbnailURL } : prev));
    }, []);

    const updatedUserDisplayName = useCallback(async (firstName, lastName) => {
        try {
            const email = userRef.current?.email;
            if (!email) return;
            await updateUser({ firstName, lastName }, email);
            const displayName = `${firstName} ${lastName}`;
            setUser((prev) =>
                prev ? { ...prev, displayName, name: displayName, firstName, lastName } : prev,
            );
        } catch (error) {
            console.error('Display name update error:', error);
            throw error;
        }
    }, []);

    const deactivateProcoreIntegration = useCallback(() => {
        setUser((prev) => (prev ? { ...prev, hasProcoreIntegration: false } : prev));
    }, []);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    useEffect(() => {
        let cancelled = false;
        fetch(`${apiBase}/auth/config`, { credentials: 'omit' })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (cancelled || !data?.branding) return;
                setBranding(mergeAuthBranding(data.branding));
            })
            .catch(() => { });
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) return <Loading />;

    return (
        <AuthContext.Provider
            value={{
                method: 'COGNITO_SESSION',
                branding,
                signIn,
                completeMfa,
                getMfaStatus,
                beginTotpSetup,
                confirmTotpSetup,
                disableTotp,
                signUp,
                confirmSignUp,
                signUpCognito,
                signInWithProvider,
                signOut,
                logout: signOut,
                hardDeleteLogout: () => setUser(null),
                getToken,
                forgotPassword,
                resetPassword,
                resendVerification,
                setProfilePicture,
                updatedUserDisplayName,
                loading,
                user,
                isAuthenticated,
                deactivateProcoreIntegration,
                checkSession,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
